import { format } from "date-fns";
import { t } from "i18next";

export interface UnavailabilityPeriod {
  from: string;
  to: string;
}

export interface TechnicianUnavailabilityExport {
  technician: {
    firstName?: string;
    lastName?: string;
  };
  totalUnavailDays: number;
  details?: UnavailabilityPeriod[];
}

export interface UnavailabilityExportLabels {
  tech: string;
  total: string;
  notes: string;
  sheet: string;
  period: (from: string, to: string) => string;
}

/** Labels in the language currently selected for the signed-in user. */
export function unavailabilityExportLabels(): UnavailabilityExportLabels {
  return {
    tech: t("technician:unavailabilityExport.tech"),
    total: t("technician:unavailabilityExport.total"),
    notes: t("technician:unavailabilityExport.notes"),
    sheet: t("technician:unavailabilityExport.sheet"),
    period: (from, to) => t("technician:unavailabilityExport.period", { from, to })
  };
}

/**
 * One row per technician for the unavailability workbook.
 * The total is the working days already clipped to the exported period.
 * Notes keep the declared periods, including days that fall outside that period.
 */
export function buildUnavailabilitySheet(
  rows: TechnicianUnavailabilityExport[],
  labels: UnavailabilityExportLabels
): (string | number)[][] {
  const body = rows.map((item) => [
    technicianName(item),
    item.totalUnavailDays || 0,
    (item.details ?? []).map((period) => periodNote(period, labels)).join(" / ")
  ]);

  return [[labels.tech, labels.total, labels.notes], ...body];
}

function technicianName(item: TechnicianUnavailabilityExport): string {
  return [item.technician.firstName, item.technician.lastName].filter(Boolean).join(" ");
}

function periodNote(period: UnavailabilityPeriod, labels: UnavailabilityExportLabels): string {
  return labels.period(
    format(new Date(period.from), "dd-MM-yyyy"),
    format(new Date(period.to), "dd-MM-yyyy")
  );
}
